'use strict'

/**
 * Map-related utility functions and constants shared across Territory Workbench.
 */

// ── Constants ─────────────────────────────────────────────────

export const MAP_CONTAINER_STYLE = { width: '100%', height: 'calc(100vh - 260px)', minHeight: 520 }
export const HMI_YARD = { lat: 33.43730763333812, lng: -112.11879940396678, label: 'HMI Yard' }
export const DEFAULT_CENTER = { lat: 33.45, lng: -112.07 } // Phoenix, AZ
export const DEFAULT_ZOOM = 10

// ── Distance / formatting helpers ─────────────────────────────

export const toMiles = (distanceKm) => distanceKm * 0.621371

export const haversineMiles = (lat1, lng1, lat2, lng2) => {
  const toRad = (value) => (value * Math.PI) / 180
  const earthRadiusKm = 6371
  const dLat = toRad(lat2 - lat1)
  const dLng = toRad(lng2 - lng1)

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2)

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return toMiles(earthRadiusKm * c)
}

export const formatMinutes = (minutes) => {
  const num = Number(minutes || 0)
  if (!Number.isFinite(num)) return '0m'
  const rounded = Math.round(num)
  const h = Math.floor(rounded / 60)
  const m = rounded % 60
  if (h <= 0) return `${m}m`
  if (m === 0) return `${h}h`
  return `${h}h ${m}m`
}

export const formatDeltaMinutes = (minutes) => {
  const value = Number(minutes || 0)
  const sign = value >= 0 ? '+' : '-'
  return `${sign}${formatMinutes(Math.abs(value))}`
}

let _warnedAddressObject = false
// Coerce an address value to a safe, renderable STRING. Address fields are normally
// strings, but some property/jobsite records arrive with a structured object (e.g.
// `{ street, city, state, country, zipCode }` from a geocode/Aspire payload in a
// flexible field). Rendering that object directly throws React's "Objects are not
// valid as a React child". This normalizes any shape and logs the first offender so
// the data source can be tracked down. (Mirror of PropertyDetail/formatAddress, kept
// here as a dependency-light leaf util for the map/route components.)
export const formatAddress = (value) => {
  if (value == null) return ''
  if (typeof value === 'string') return value
  if (Array.isArray(value)) return value.filter(Boolean).map(formatAddress).join(', ')
  if (typeof value === 'object') {
    if (!_warnedAddressObject && typeof console !== 'undefined') {
      _warnedAddressObject = true
      // eslint-disable-next-line no-console
      console.warn('[formatAddress] received an object address (expected string); coercing. Value:', value)
    }
    const street = value.street || value.address || value.address1 || ''
    const city = value.city || ''
    const state = value.state || ''
    const zip = value.zipCode || value.zip || value.zipcode || ''
    const cityStateZip = [city, [state, zip].filter(Boolean).join(' ')].filter(Boolean).join(', ')
    return [street, cityStateZip].filter(Boolean).join(', ')
  }
  return String(value)
}
